import { useCallback, useSyncExternalStore } from 'react';

/**
 * Lo que va detrás de la dirección de una sección (/ligas/<id>,
 * /admin/usuarios…) lo lleva la propia sección: así su vista interna también
 * sobrevive a recargar y Atrás/Adelante la recorre.
 *
 * App solo conoce la base de cada sección; deja en paz lo que cuelgue de ella
 * (ver SUBPATH_SCREENS en routes.ts).
 */

const EVENT = 'dahu:locationchange';

/** Avisa a las secciones montadas de que la dirección cambió sin popstate. */
export function notifyLocationChange(): void {
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener('popstate', cb);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(EVENT, cb);
  };
}

const getPath = () => location.pathname;

interface SetOpts {
  replace?: boolean;
  keepScroll?: boolean;
}

/**
 * Segmentos que siguen a `base` en la dirección actual y una función para
 * cambiarlos. `replace` sustituye la entrada del historial en vez de apilar
 * otra (para correcciones que no son una navegación de verdad); `keepScroll`
 * no sube al principio (cambiar de pestaña sin perder de vista las pestañas).
 */
export function useSubPath(base: string): [string[], (segments: string[], opts?: SetOpts) => void] {
  const path = useSyncExternalStore(subscribe, getPath);
  const prefix = base + '/';
  const segments = path.startsWith(prefix)
    ? path
        .slice(prefix.length)
        .split('/')
        .filter(Boolean)
        .map((s) => {
          try {
            return decodeURIComponent(s);
          } catch {
            return s;
          }
        })
    : [];

  const set = useCallback(
    (next: string[], opts?: SetOpts) => {
      const target = next.length ? `${base}/${next.map(encodeURIComponent).join('/')}` : base;
      if (target === location.pathname) return;
      if (opts?.replace) window.history.replaceState({}, '', target);
      else window.history.pushState({}, '', target);
      notifyLocationChange();
      if (!opts?.keepScroll) window.scrollTo(0, 0);
    },
    [base],
  );

  return [segments, set];
}
